import { useState, type FormEvent } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { submitPartnerInquiry, type PartnerInquiryInput } from '@workspace/api-client-react';

export default function PartnerInquiry() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const input: PartnerInquiryInput = {
      fullNames: String(form.get('fullNames') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      country: String(form.get('country') ?? '').trim(),
      city: String(form.get('city') ?? '').trim(),
      subject: form.get('subject') as PartnerInquiryInput['subject'],
    };
    setSubmitting(true);
    setStatus('');
    setFailed(false);
    try {
      const result = await submitPartnerInquiry(input);
      setStatus(result.message);
      formElement.reset();
    } catch (error) {
      setFailed(true);
      const data = error && typeof error === 'object' && 'data' in error ? error.data : null;
      const serverMessage = data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error : null;
      setStatus(serverMessage || (error instanceof Error ? error.message : 'Your inquiry could not be delivered. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return <main className="commissions-page">
    <section className="commissions-intro">
      <span className="eyebrow">Invest in Us / Partner with Us</span>
      <h1 className="display">Be part of<br /><em>something singular.</em></h1>
      <p>One Exclusive Club curates original, one-of-one residential architectural concepts and offers private architectural guidance and bespoke design.</p>
      <p>We welcome conversations with potential investors who want to support our growth and partners who see opportunities to collaborate. Tell us who you are and select your interest below. Our team will review your inquiry privately and contact you to discuss what may be possible.</p>
    </section>
    <form className="commissions-form" onSubmit={submit} aria-label="Partnership or investment inquiry" aria-busy={submitting}>
      <div className="form-field">
        <label htmlFor="partner-names">Full Names</label>
        <input id="partner-names" name="fullNames" required minLength={2} maxLength={200} autoComplete="name" />
      </div>
      <div className="form-field">
        <label htmlFor="partner-email">Email Address</label>
        <input id="partner-email" name="email" type="email" required maxLength={254} autoComplete="email" />
      </div>
      <div className="form-field">
        <label htmlFor="partner-phone">Phone Number</label>
        <input id="partner-phone" name="phone" type="tel" required minLength={7} maxLength={60} autoComplete="tel" placeholder="Include your country code" />
      </div>
      <div className="form-field">
        <label htmlFor="partner-country">Country</label>
        <input id="partner-country" name="country" required minLength={2} maxLength={120} autoComplete="country-name" />
      </div>
      <div className="form-field">
        <label htmlFor="partner-city">City</label>
        <input id="partner-city" name="city" required minLength={2} maxLength={120} autoComplete="address-level2" />
      </div>
      <div className="form-field">
        <label htmlFor="partner-subject">Subject</label>
        <select id="partner-subject" name="subject" required defaultValue="">
          <option value="" disabled>Select your interest</option>
          <option value="Partnership with Us">Partnership with Us</option>
          <option value="Investing in Us">Investing in Us</option>
        </select>
      </div>
      {status && <p className="commission-status" role={failed ? 'alert' : 'status'}>{status}</p>}
      <button className="quiet-button quiet-button--solid" disabled={submitting} type="submit">
        {submitting ? 'Sending inquiry' : 'Submit inquiry'} <ArrowUpRight size={15} />
      </button>
    </form>
  </main>;
}
